import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import BZAPIService from '../../services/BZAPIService';
import { VendorMark } from '../bsa/projects/ProjectAtoms';
import InventoryVendor from './PredictInventoryVendor';
import { IcoArrowR } from '../bz/icons';
import { PageHead, PredictCard, ScoreBadge, StatTile } from './PredictAtoms';
import {
  PROMOTION_SCORE,
  formatCount,
  formatDate,
  formatDateTime,
  formatScore,
  isPromoted,
  trackedNumbers
} from './predictWatchlistModel';
import {
  CLASS_LABEL,
  WATCH_PATH,
  caughtCounts,
  projectRows,
  recentOutcomes,
  shownOutcomes,
  watchListPath
} from './predictDashboardModel';
import { bugPagePath } from './predictBugModel';
import { sourceLabel } from './predictSources';

const small = { fontSize: 'var(--fs-sm)' };

const LOADING = { status: 'loading', body: null };

const PILL_TONE = { predicted: 'bz-pill--predict', 'retro-matched': 'bz-pill--info' };

const Loading = ({ label }) => (
  <div className="pdp-empty" role="status">
    <Spinner color="primary" size="sm" /> {label}
  </div>
);
Loading.propTypes = { label: PropTypes.string.isRequired };

const Unavailable = ({ what, onRetry }) => (
  <div className="pdp-empty">
    <b>{what} is unavailable right now.</b>
    <button type="button" className="bz-btn bz-btn--sm" style={{ marginTop: 8 }} onClick={onRetry}>
      Try again
    </button>
  </div>
);
Unavailable.propTypes = { what: PropTypes.string.isRequired, onRetry: PropTypes.func.isRequired };

// The project table: a row opens the watch list filtered to the project. The
// name is the link a keyboard reaches; the rest of the row clicks through too.
const ProjectsTable = ({ rows }) => {
  const navigate = useNavigate();
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="bz-table pdp-projects">
        <thead>
          <tr>
            <th>Vendor</th>
            <th>OSS</th>
            <th className="pdp-col-wide">Source</th>
            <th className="pdp-num">Bugs</th>
            <th>Top score</th>
            <th className="pdp-col-wide">Newest filed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const to = watchListPath(row.vendors);
            return (
              <tr key={row.target} data-testid="project-row" onClick={() => navigate(to)}>
                <td data-testid="inventory-vendor">
                  <InventoryVendor vendorId={row.inventoryVendorId} products={row.inventoryProducts} />
                </td>
                <td>
                  <div className="bz-row" style={{ gap: 8 }}>
                    <VendorMark vendorId={row.vendors[0] || row.target} label={row.name} size={22} />
                    <Link to={to} onClick={e => e.stopPropagation()} style={{ fontWeight: 500 }}>
                      {row.name}
                    </Link>
                  </div>
                </td>
                <td className="pdp-col-wide bz-muted" style={small}>
                  {row.source ? sourceLabel(row.source) : '—'}
                </td>
                <td className="pdp-num bz-mono">{row.count}</td>
                <td>
                  <ScoreBadge score={row.topScore} />
                </td>
                <td className="pdp-col-wide bz-mono" style={{ ...small, whiteSpace: 'nowrap' }}>
                  {formatDate(row.newestFiled)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
ProjectsTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      vendors: PropTypes.arrayOf(PropTypes.string).isRequired,
      count: PropTypes.number.isRequired,
      topScore: PropTypes.number,
      newestFiled: PropTypes.string,
      source: PropTypes.string,
      inventoryVendorId: PropTypes.string,
      inventoryProducts: PropTypes.arrayOf(PropTypes.string)
    })
  ).isRequired
};

const CaughtCard = ({ outcomes, threshold, onRetry }) => {
  const counts = outcomes.status === 'ready' ? caughtCounts(outcomes.list) : null;
  const none = counts && counts.predicted === 0 && counts['retro-matched'] === 0;
  return (
    <PredictCard title="Caught in your view" labelledBy="pdp-caught">
      {outcomes.status === 'loading' && <Loading label="Loading CVE matches…" />}
      {outcomes.status === 'failed' && <Unavailable what="CVE matching" onRetry={onRetry} />}
      {counts && none && (
        <div className="pdp-empty" data-testid="caught-empty">
          <b>No caught bugs yet.</b>
          Predict hasn&apos;t matched a CVE to any of your bugs at or above {formatScore(threshold)} yet.
        </div>
      )}
      {counts && !none && (
        <div className="pdp-card-body">
          <div className="bz-row" style={{ gap: 28, flexWrap: 'wrap' }}>
            {Object.keys(CLASS_LABEL).map(cls => (
              <div key={cls} role="group" aria-label={`${CLASS_LABEL[cls]}s`}>
                <div className="bz-stat__value" data-testid={`caught-${cls}`}>
                  {counts[cls]}
                </div>
                <span className={`bz-pill ${PILL_TONE[cls]}`} style={{ marginTop: 4 }}>
                  {counts[cls] === 1 ? CLASS_LABEL[cls] : `${CLASS_LABEL[cls]}s`}
                </span>
              </div>
            ))}
          </div>
          <div className="bz-muted" style={{ ...small, marginTop: 10 }}>
            Bugs at or above your threshold that later became CVEs. Predictions and detections are counted apart.
          </div>
        </div>
      )}
    </PredictCard>
  );
};
CaughtCard.propTypes = {
  outcomes: PropTypes.shape({ status: PropTypes.string.isRequired, list: PropTypes.array }).isRequired,
  threshold: PropTypes.number,
  onRetry: PropTypes.func.isRequired
};

const leadText = days => {
  if (!Number.isInteger(days)) return null;
  const n = Math.abs(days);
  return `${n} ${n === 1 ? 'day' : 'days'} ${days < 0 ? 'before' : 'after'} the freeze`;
};

const RecentMatches = ({ outcomes, onRetry }) => {
  const recent = outcomes.status === 'ready' ? recentOutcomes(outcomes.list) : [];
  return (
    <PredictCard title="Recent CVE matches" labelledBy="pdp-recent">
      {outcomes.status === 'loading' && <Loading label="Loading CVE matches…" />}
      {outcomes.status === 'failed' && <Unavailable what="CVE matching" onRetry={onRetry} />}
      {outcomes.status === 'ready' && recent.length === 0 && (
        <div className="pdp-empty" data-testid="recent-empty">
          <b>No CVE matches yet.</b>
          When a bug on your list becomes a CVE, it shows here.
        </div>
      )}
      {recent.length > 0 && (
        <ul className="pdp-list">
          {recent.map(outcome => (
            <li key={`${outcome.vendor}\u0000${outcome.bugId}\u0000${outcome.cve}`} data-testid="recent-match">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="bz-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span className="bz-mono" style={{ fontWeight: 600 }}>
                    {outcome.cve}
                  </span>
                  <span className={`bz-pill ${PILL_TONE[outcome.class]}`}>{CLASS_LABEL[outcome.class]}</span>
                </div>
                <div style={{ ...small, marginTop: 2, overflowWrap: 'anywhere' }}>
                  <Link to={bugPagePath(outcome.vendor, outcome.bugId)}>{outcome.title || outcome.bugId}</Link>
                </div>
                <div className="bz-muted" style={small}>
                  {[outcome.targetName, formatDate(outcome.firstPublicAt), leadText(outcome.leadDays)]
                    .filter(part => part && part !== '—')
                    .join(' · ')}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </PredictCard>
  );
};
RecentMatches.propTypes = {
  outcomes: PropTypes.shape({ status: PropTypes.string.isRequired, list: PropTypes.array }).isRequired,
  onRetry: PropTypes.func.isRequired
};

const settle = (promise, set, label) =>
  promise
    .then(body => set({ status: 'ready', body: body || null }))
    .catch(error => {
      console.error(`Failed to load the Predict ${label}:`, error);
      set({ status: 'failed', body: null });
    });

/**
 * Predict → Overview: the dashboard (STRY0039090).
 *
 * The customer's watch list at a glance, from the same GET /predict/watchlist
 * as the watch list, so its numbers match: how many bugs are on it, and by
 * project, where a row opens the watch list filtered to that project. Beside
 * it, what BugZero tracks (STRY0039127): the bugs in BugZero's archive and
 * those of the customer's projects (GET /predict/counts and the watch list's
 * `archive`), and what Predict scored and promoted since its first run, at
 * 6.0 for every customer; the projects in scope (GET /predict/scope) and the
 * bugs that became CVEs
 * (GET /predict/outcomes), Predictions and Detections each on their own. Eric's
 * rule: no rates, percentages or 90/180-day reads. Each source loads and fails
 * on its own.
 *
 * Real data only; the route sits behind <ProductGuard product="predict">.
 */
const PredictDashboard = () => {
  const [watch, setWatch] = useState(LOADING);
  const [counts, setCounts] = useState(LOADING);
  const [scope, setScope] = useState(LOADING);
  const [outcomes, setOutcomes] = useState(LOADING);
  const loadId = useRef(0);

  // All four load side by side; a Refresh started since wins.
  const load = useCallback(() => {
    const id = ++loadId.current;
    const only = set => state => {
      if (id === loadId.current) set(state);
    };
    [setWatch, setCounts, setScope, setOutcomes].forEach(set => set(LOADING));
    settle(BZAPIService.getPredictWatchlist(), only(setWatch), 'watch list');
    settle(BZAPIService.getPredictCounts(), only(setCounts), 'counts');
    settle(BZAPIService.getPredictScope(), only(setScope), 'scope');
    settle(BZAPIService.getPredictOutcomes(), only(setOutcomes), 'outcomes');
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const bugs = useMemo(
    () => (Array.isArray(watch.body?.bugs) ? watch.body.bugs.filter(b => b && b.vendor && b.bugId) : []),
    [watch.body]
  );
  const rows = useMemo(() => projectRows(bugs), [bugs]);
  const threshold = typeof watch.body?.threshold === 'number' ? watch.body.threshold : null;
  const belowPromotion = bugs.filter(bug => !isPromoted(bug)).length;
  const scopeCount = Array.isArray(scope.body?.entries) ? scope.body.entries.length : null;
  const shown = useMemo(() => ({ status: outcomes.status, list: shownOutcomes(outcomes.body?.outcomes) }), [
    outcomes.status,
    outcomes.body
  ]);
  const watchReady = watch.status === 'ready';
  const anyLoading = [watch, counts, scope, outcomes].some(s => s.status === 'loading');

  return (
    <div className="pdp-page">
      <PageHead
        kicker="Predict · Overview"
        title="Dashboard"
        sub={
          <>
            Your watch list at a glance: the bugs in your scope at or above your threshold.
            {watch.body?.asOf ? ` Updated ${formatDateTime(watch.body.asOf)}.` : ''}
          </>
        }
      >
        <button type="button" className="bz-btn bz-btn--sm" onClick={load} disabled={anyLoading}>
          <FontAwesomeIcon icon={faRefresh} className={anyLoading ? 'fa-spin' : ''} />
          {' Refresh'}
        </button>
        <Link to={WATCH_PATH} className="bz-btn bz-btn--sm bz-btn--primary">
          Open watch list <IcoArrowR size={13} />
        </Link>
      </PageHead>

      <div className="pdp-stats">
        <StatTile
          label="On your watch list"
          testId="kpi-watchlist"
          value={watchReady ? formatCount(bugs.length) : '—'}
          sub={
            watchReady
              ? `At or above your threshold, ${formatScore(threshold)}${
                  belowPromotion ? ` · ${belowPromotion} below ${formatScore(PROMOTION_SCORE)}, not promoted` : ''
                }`
              : watch.status === 'failed'
              ? 'Unavailable right now'
              : 'Loading…'
          }
        />
        <StatTile
          label="In scope"
          testId="kpi-scope"
          value={scopeCount === null ? '—' : formatCount(scopeCount)}
          sub={
            <>
              {watchReady ? `${rows.length} with bugs on your list · ` : ''}
              <Link to="/predict/coverage">See coverage</Link>
            </>
          }
        />
        {trackedNumbers(counts.body, watch.body?.archive).map(({ key, label, value, note }) => (
          <StatTile key={key} label={label} testId={`kpi-${key}`} value={formatCount(value)} sub={note} />
        ))}
      </div>

      <div className="pdp-split">
        <PredictCard
          title="Your watch list by OSS"
          labelledBy="pdp-projects"
          right={
            watchReady && rows.length > 0 ? (
              <span className="bz-muted" style={small}>
                {rows.length} OSS · {bugs.length} {bugs.length === 1 ? 'bug' : 'bugs'}
              </span>
            ) : null
          }
        >
          {watch.status === 'loading' && <Loading label="Loading the watch list…" />}
          {watch.status === 'failed' && <Unavailable what="Predict data" onRetry={load} />}
          {watchReady && rows.length === 0 && (
            <div className="pdp-empty" data-testid="projects-empty">
              <b>No bugs on your watch list.</b>
              No bug in your scope scores {formatScore(threshold)} or higher yet. Predict scores new bugs once a day.
            </div>
          )}
          {watchReady && rows.length > 0 && (
            <>
              <ProjectsTable rows={rows} />
              <div className="bz-muted" style={{ ...small, padding: '10px 16px' }}>
                Open one to see its bugs on the watch list.
              </div>
            </>
          )}
        </PredictCard>

        <div className="pdp-rail">
          <CaughtCard outcomes={shown} threshold={threshold} onRetry={load} />
          <RecentMatches outcomes={shown} onRetry={load} />
        </div>
      </div>
    </div>
  );
};

export default PredictDashboard;
